import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { api, ApiError } from '../lib/api'
import type { AppSettings } from '../types'

export interface Me {
  id: number
  email: string
  name: string
  role: string
}

/** /settings answers without a session, so it doubles as the open-access signal. */
export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<AppSettings>('/settings'),
    retry: false,
  })
}

/**
 * Deliberately switched off while open access is on: there is no session to report,
 * so every call would 401.
 *
 * That is not merely noise. A failed query holds no data, so React Query sends it from
 * `error` back to `pending` whenever a second component subscribes and triggers a
 * refetch. RequireAuth gates on that status, so the refetch unmounted the very component
 * whose mount caused it, which settled the query, which remounted it - an endless loop
 * that pinned the page on the loading splash. Never gate rendering on a shared query that
 * can return to `pending`.
 */
export function useMe() {
  const settings = useSettings()
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
    enabled: settings.data?.open_access === false,
  })
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const settings = useSettings()
  const { data, isLoading, error } = useMe()

  if (settings.isLoading) {
    return <div className="bootsplash">Checking your session…</div>
  }
  // Answered before anything looks at the session, so no session state can unmount us.
  if (settings.data?.open_access) {
    return <>{children}</>
  }
  if (isLoading) {
    return <div className="bootsplash">Checking your session…</div>
  }
  if (error instanceof ApiError && error.status === 401) {
    return <Navigate to="/login" replace />
  }
  if (!data) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}
